import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDeleteUsers } from '../../../hooks/admin/useUsers'
import ConfirmDeleteDialog from '../ConfirmDeleteDialog'

interface DangerTabProps {
  userId: number
  userLabel: string
  /** An admin cannot delete their own account; the tab is not offered either. */
  isEditingSelf: boolean
}

export function DangerTab({ userId, userLabel, isEditingSelf }: DangerTabProps) {
  const navigate = useNavigate()
  const deleteUsers = useDeleteUsers()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  // Belt and braces: the tab is hidden for self-editing, but the route can
  // still be typed, and a delete is not something to leave to the nav.
  if (isEditingSelf) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-2">Danger Zone</h2>
        <p className="text-gray-600">You cannot delete the account you are signed in as.</p>
      </div>
    )
  }

  const handleDelete = async () => {
    try {
      await deleteUsers.mutateAsync([userId])
      setShowDeleteDialog(false)
      navigate('/admin/users')
    } catch (error) {
      console.error('Failed to delete user:', error)
    }
  }

  return (
    <>
      <div className="bg-white border border-red-300 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-red-700 mb-2">Danger Zone</h2>
        <p className="text-gray-600 mb-4 max-w-2xl">
          Deleting this user will remove all of their group access. This action cannot be undone.
        </p>
        <button
          type="button"
          onClick={() => setShowDeleteDialog(true)}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Delete User
        </button>
      </div>

      <ConfirmDeleteDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Delete User"
        message={`This will permanently delete ${userLabel} and remove all of their group access. This cannot be undone.`}
        confirmText={userLabel}
        isLoading={deleteUsers.isPending}
      />
    </>
  )
}
